import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";

const EMPTY = {
  ticker: "",
  fullname: "",
  company_name: "",
  par_value: 25,
  coupon_rate: "",
  payment_frequency: 4,
  first_ex_div_date: "",
  first_payment_date: "",
  confirmed_ex_div_dates: "",
  confirmed_payment_dates: "",
  maturity_date: "",
  call_date: "",
  total_outstanding_usd: "",
  market_cap_usd: "",
  notes: "",
};

export default function MasterBondDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [fetchingMc, setFetchingMc] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      ...EMPTY,
      ...editing,
      total_outstanding_usd: editing.total_outstanding_usd ?? "",
      market_cap_usd: editing.market_cap_usd ?? "",
      confirmed_ex_div_dates: (editing.confirmed_ex_div_dates || []).join(", "),
      confirmed_payment_dates: (editing.confirmed_payment_dates || []).join(", "),
    });
    else setForm(EMPTY);
  }, [editing, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const parseDateList = (s) => (s || "").split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
      const payload = {
        ...form,
        par_value: Number(form.par_value),
        coupon_rate: Number(form.coupon_rate),
        payment_frequency: Number(form.payment_frequency),
        total_outstanding_usd: form.total_outstanding_usd === "" ? null : Number(form.total_outstanding_usd),
        market_cap_usd: form.market_cap_usd === "" ? null : Number(form.market_cap_usd),
        first_ex_div_date: form.first_ex_div_date || null,
        first_payment_date: form.first_payment_date || null,
        call_date: form.call_date || null,
        confirmed_ex_div_dates: parseDateList(form.confirmed_ex_div_dates),
        confirmed_payment_dates: parseDateList(form.confirmed_payment_dates),
      };
      if (editing?.id) {
        await api.put(`/catalog/${editing.id}`, payload);
        toast.success(`${payload.ticker || editing.ticker} updated`);
      } else {
        await api.post("/catalog", payload);
        toast.success("Bond added to catalog");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const fetchMarketCap = async () => {
    const sym = form.ticker?.trim().toUpperCase();
    if (!sym) { toast.error("Enter ticker first"); return; }
    setFetchingMc(true);
    try {
      const { data } = await api.get(`/quote/overview/${sym}`);
      setForm(f => ({ ...f, market_cap_usd: data.market_cap_usd }));
      toast.success(`Fetched ${data.name || sym} market cap`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't fetch — try the parent equity ticker");
    } finally { setFetchingMc(false); }
  };

  const inputCls = "w-full bg-white border border-stone px-3 py-2 text-sm font-mono text-graphite focus:border-paars-dark focus:outline-none";
  const labelCls = "text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!rounded-none bg-white border border-stone max-w-3xl text-graphite p-0 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="px-6 py-4 border-b border-stone">
          <DialogTitle className="text-base font-mono uppercase tracking-widest text-paars-dark">
            {editing ? "// EDIT MASTER BOND" : "// ADD MASTER BOND"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Ticker *</label>
              <input data-testid="mb-ticker" required value={form.ticker} onChange={(e)=>set("ticker", e.target.value.toUpperCase())} className={inputCls} placeholder="ATLCL" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Full Name</label>
              <input value={form.fullname} onChange={(e)=>set("fullname", e.target.value)} className={inputCls} placeholder="Atlanticus Holdings 6.125% Sr Notes due 2029" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Company Name</label>
              <input value={form.company_name} onChange={(e)=>set("company_name", e.target.value)} className={inputCls} placeholder="Atlanticus Holdings Corp" />
            </div>
            <div>
              <label className={labelCls}>Outstanding Debt (USD)</label>
              <input type="number" step="1" value={form.total_outstanding_usd} onChange={(e)=>set("total_outstanding_usd", e.target.value)} className={inputCls} placeholder="75000000" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Market Cap (USD)</label>
            <div className="flex gap-2">
              <input data-testid="mb-market-cap" type="number" step="1" value={form.market_cap_usd} onChange={(e)=>set("market_cap_usd", e.target.value)} className={inputCls} placeholder="425000000" />
              <button type="button" onClick={fetchMarketCap} disabled={fetchingMc} className="px-3 py-2 border border-paars text-paars-dark text-xs hover:bg-paars/20 whitespace-nowrap disabled:opacity-50">
                {fetchingMc ? "FETCHING..." : "FETCH FROM AV"}
              </button>
            </div>
            <div className="text-[10px] text-graphite/45 mt-1 font-mono">Tip: Alpha Vantage OVERVIEW often only indexes the parent equity ticker — for senior notes (e.g. ATLCL) enter the parent ticker (e.g. ATLC) in the field above, click FETCH, then restore the bond ticker.</div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Par Value *</label>
              <input data-testid="mb-par" required type="number" step="0.01" value={form.par_value} onChange={(e)=>set("par_value", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Coupon Rate (%) *</label>
              <input data-testid="mb-coupon" required type="number" step="0.001" value={form.coupon_rate} onChange={(e)=>set("coupon_rate", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Payments / Year *</label>
              <select required value={form.payment_frequency} onChange={(e)=>set("payment_frequency", e.target.value)} className={inputCls}>
                <option value={1}>1 (Annual)</option>
                <option value={2}>2 (Semi)</option>
                <option value={4}>4 (Quarterly)</option>
                <option value={12}>12 (Monthly)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Maturity Date *</label>
              <input data-testid="mb-maturity" required type="date" value={form.maturity_date} onChange={(e)=>set("maturity_date", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Call Date</label>
              <input type="date" value={form.call_date} onChange={(e)=>set("call_date", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First Ex-Div Date <span className="opacity-50">(pattern anchor)</span></label>
              <input type="date" value={form.first_ex_div_date} onChange={(e)=>set("first_ex_div_date", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>First Payment Date <span className="opacity-50">(pattern anchor)</span></label>
              <input type="date" value={form.first_payment_date} onChange={(e)=>set("first_payment_date", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Confirmed Ex-Div Dates <span className="opacity-50">(comma-separated, overrides pattern)</span></label>
            <textarea data-testid="mb-confirmed-exdiv" value={form.confirmed_ex_div_dates} onChange={(e)=>set("confirmed_ex_div_dates", e.target.value)} className={inputCls} rows={2} placeholder="2026-08-12, 2026-11-12" />
          </div>

          <div>
            <label className={labelCls}>Confirmed Payment Dates <span className="opacity-50">(comma-separated, overrides pattern)</span></label>
            <textarea data-testid="mb-confirmed-pay" value={form.confirmed_payment_dates} onChange={(e)=>set("confirmed_payment_dates", e.target.value)} className={inputCls} rows={2} placeholder="2026-08-26, 2026-11-26" />
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e)=>set("notes", e.target.value)} className={inputCls} rows={2} />
          </div>

          <DialogFooter className="!flex !justify-end gap-2 pt-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-5 py-2.5 border border-stone text-graphite/80 text-sm hover:bg-stone/50">Cancel</button>
            <button data-testid="mb-save" type="submit" disabled={saving} className="px-5 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light disabled:opacity-50">
              {saving ? "SAVING..." : editing ? "UPDATE BOND" : "ADD TO CATALOG"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
